import {
  readRootFormFunctions,
  readRootFormOnLoadScript,
  resolveRootFormPageIdFromLayout,
  shouldRunRootFormOnLoadOnClientBeforeBuild,
} from '@/common/utils/formRootOnLoad'
import { mergeExchangeStatesByKey, normalizeExchangeStates } from '@/common/utils/normalizeExchangeStates'
import { normalizePlatformSessionId } from '@/common/utils/jsRequestSessionContext'
import { resolveLayoutScopedClientFields } from '@/common/utils/layoutScopedClientFields'
import { applyExchangeDataToAppStoreSlot } from '@/services/action/applyChangedDataHelpers'
import type { ExchangeData } from '@/services/action/types'
import { scriptService } from '@/services/script/script.service'
import { ScriptLevel, ScriptName } from '@/services/websocket/types'
import type { useAppStore } from '@/stores/app'

type AppStore = ReturnType<typeof useAppStore>

function buildCurrentDataForOnLoad(pageData: Record<string, unknown>): ExchangeData {
  const fields = resolveLayoutScopedClientFields(pageData, pageData.form)
  return {
    fields: {
      fields: fields ?? {},
    },
  } as ExchangeData
}

/**
 * SERVICE_ACTION с deferServerOnLoad не выполняет onLoad на middle при open.
 * Корневой onLoad без web_client_exec нужно догнать через executeScript (startup SERVICE_ACTION с deferServerOnLoad).
 */
export async function runStartupServerRootOnLoadIfNeeded(
  appStore: AppStore,
  slotId: string,
  pageData: Record<string, unknown>,
): Promise<void> {
  const form = pageData.form
  if (!form || typeof form !== 'object' || shouldRunRootFormOnLoadOnClientBeforeBuild(form)) {
    return
  }

  const script = readRootFormOnLoadScript(form)
  if (!script?.trim()) {
    return
  }

  const sessionId = normalizePlatformSessionId(pageData.sessionId)
  if (!sessionId) {
    return
  }

  const pageId = resolveRootFormPageIdFromLayout(form)
  if (!pageId) {
    console.warn('[StartupAction] server onLoad skipped: root page id not found', slotId)
    return
  }

  const functions = readRootFormFunctions(form)
  const scriptBody = functions?.trim() ? `${functions}\n${script}` : script

  try {
    const response = await scriptService.executeScript({
      sessionId,
      elementId: pageId,
      level: ScriptLevel.PAGE,
      scriptName: ScriptName.ON_LOAD,
      scriptBody,
      currentData: buildCurrentDataForOnLoad(pageData),
    })

    const states = normalizeExchangeStates(response)
    applyExchangeDataToAppStoreSlot(
      appStore,
      slotId,
      response?.changed_data,
      'startupAction.serverRootOnLoad',
      undefined,
      states,
    )

    const fresh = appStore.pagesData[slotId] as Record<string, unknown> | undefined
    if (!fresh) {
      return
    }

    const prevStates = Array.isArray((fresh.jsResult as { states?: unknown[] } | undefined)?.states)
      ? ((fresh.jsResult as { states: unknown[] }).states as unknown[])
      : []

    appStore.setPageData(
      slotId,
      {
        ...fresh,
        jsResult: {
          ...(fresh.jsResult as Record<string, unknown> | undefined),
          states: mergeExchangeStatesByKey(prevStates, states),
          changed_data: response?.changed_data ?? (fresh.jsResult as { changed_data?: unknown } | undefined)?.changed_data,
          event_listeners:
            response?.event_listeners ??
            (fresh.jsResult as { event_listeners?: unknown } | undefined)?.event_listeners,
        },
      },
      fresh.$parentPageId as string | undefined,
      undefined,
      'startupAction.serverRootOnLoad',
    )
  } catch (error) {
    console.warn('[StartupAction] server root onLoad failed', slotId, error)
  }
}
